<template>
  <div class="row mb-4">
    <div class="col">
      <DataTable
          :value="tableItems"
          v-model:expandedRows="expandedRows"
          data-key="id"
          :striped-rows="true"
          :removableSort="true"
          :paginator="true"
          :alwaysShowPaginator="false"
          :rowHover="true"
          :rows="perPage"
          :total-records="tableItems.length"
          size="small"
      >
        <template #expansion="{ data }">
          <div class="event-details">
            <div>
              <div class="flex align-items-center mb-2 text14-gray">
                <Checkbox
                    v-if="data.someEventsEnded"
                    v-model="showPreviousEventsByRow[data.id]"
                    :binary="true"
                    :input-id="'show-past-' + data.id"
                />
                <label v-if="data.someEventsEnded" :for="'show-past-' + data.id" class="ms-2">
                  {{ t("SHOW_PAST_EVENTS") }}
                </label>
              </div>

              <div v-if="!showPreviousEventsByRow[data.id] && data.allEventsEnded">
                {{ t("STUDY_WORK_ENDED") }}
              </div>

              <template v-for="eventDetails in data.groupedEvents" :key="eventDetails.date + eventDetails.times.join()">
                <div v-if="showPreviousEventsByRow[data.id] || !eventDetails.isEnded" :class="{ 'event-row': true, 'event-ended': eventDetails.isEnded }">
                  {{ t("DAY_DATE_AT_TIME", {day: eventDetails.day, date: eventDetails.date, time: eventDetails.times.join(", ")}) }}
                </div>
              </template>
            </div>
          </div>
        </template>

        <Column field="moduleName" :header="t('MODULE')" sortable sortField/>
        <Column field="dates" :header="t('DATES')" sortField="datesIso" sortable/>

        <Column :header="t('TIME')">
          <template #body="{ data }">
            <Button
                v-if="data.training?.type === TrainingType.Recurring"
                class="p-0"
                icon="pi pi-calendar"
                variant="link"
                :label="t('SHOW_TIMETABLE')"
                @click="toggleRowExpand(data)"
            />
            <div v-else v-for="(eventDetails, idx) in data.groupedEvents" :key="idx">
              {{ t("DAY_AT_TIME", {day: eventDetails.day, time: eventDetails.times.join(", ")}) }}
            </div>
          </template>
        </Column>

        <Column field="language" :header="t('LANGUAGE')" sortable/>
        <Column field="location" :header="t('LOCATION')" sortable/>
        <Column field="numberOfVacancies" :header="t('NUMBER_OF_VACANCIES')" sortable v-if="register"/>

        <Column field="isPassed" :header="t('RESULT')" v-if="showResult">
          <template #body="{ data }">
            <span :class="data.isPassed ? 'text-success' : 'text-danger'">
              {{ data.isPassed ? t("PASSED") : t("NOT_PASSED") }}
            </span>
          </template>
        </Column>

        <Column>
          <template #body="{ data }">
            <div class="align-items-center" v-if="register">
              <Button :label="t('REGISTER')" icon="pi pi-user-plus" @click="showConfirmRegistrationModal(data)"/>
            </div>
            <div class="align-items-center" v-if="!register && data.status !== 'ARCHIVED'">
              <Button :label="t('CANCEL')" icon="pi pi-times" severity="danger" @click="showCancellationModal(data)"/>
            </div>
          </template>
        </Column>

        <template #empty>
          {{ t('NO_RESULTS') }}
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import moment from "moment-timezone";
import {computed, ref} from "vue";
import {GroupLocation} from "@/model/group-location";
import {injectAuth} from "@/util/inject";
import {useToast} from "@/util/toastUtils";
import {TrainingType} from "@/model/training";
import {Role} from "@/auth/oauth";
import {useDateFormat} from "@/mixins/dateFormatMixin2.ts";
import {getGroupLangOptions} from "@/model/group.js";

const auth = injectAuth();
const toast = useToast();

const props = defineProps({
  filterText: String,
  filterStatus: String,
  items: Array,
  register: {
    type: Boolean,
    default: false,
  },
  showResult: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["request-registration", "request-cancellation"]);

const {t, locale} = useI18n();
const {dateFormat, dateFormatIso} = useDateFormat();
const perPage = 7;
const expandedRows = ref({});
const showPreviousEventsByRow = ref({});
const isTrainee = computed(() => auth.hasRole(Role.TRAINEE));

function toggleRowExpand(row) {
  const id = row.id;
  const next = {...expandedRows.value};

  if (next[id]) {
    delete next[id];
  } else {
    next[id] = true;
  }

  expandedRows.value = next;
}

const showConfirmRegistrationModal = (group) => {
  if (isTrainee.value) {
    emit("request-registration", group);
  } else {
    toast.makeWarningToast(t("ACCESS_RESTRICTED"));
  }
};

const showCancellationModal = (group) => {
  if (isTrainee.value) {
    emit("request-cancellation", group);
  } else {
    toast.makeWarningToast(t("ACCESS_RESTRICTED"));
  }
};

function getGroupDates(item, formatFunc) {
  if (!item?.events || item.events.length === 0) {
    return "";
  }

  if (item.events.length === 1) {
    return formatFunc(item.events[0].startTime);
  }

  const first = item.events[0];
  const last = item.events[item.events.length - 1];
  return `${formatFunc(first.startTime)} - ${formatFunc(last.endTime)}`;
}

const getEventsGroupedByDate = (item) => {
  const results = [];
  const now = moment();

  for (const event of item.events || []) {
    const startTime = moment(event.startTime);
    const endTime = moment(event.endTime);
    const date = startTime.format("DD.MM.YY");
    const time = `${startTime.format("HH:mm")} - ${endTime.format("HH:mm")}`;

    let dateResult = results.find((r) => r.date === date);

    if (!dateResult) {
      dateResult = {
        date,
        day: t(startTime.format("dddd").toUpperCase()),
        isEnded: true,
        times: [],
      };

      results.push(dateResult);
    }

    dateResult.times.push(time);
    dateResult.isEnded = dateResult.isEnded && endTime.isBefore(now);
  }

  return results;
};

const tableItems = computed(() => {
  let items = props.items || [];

  if (props.filterText) {
    const kw = props.filterText.toLowerCase();
    items = items.filter(
        (item) =>
            item.training?.translations?.[locale.value]?.toLowerCase().includes(kw) ||
            item.language?.toLowerCase().includes(kw) ||
            item.location?.toLowerCase().includes(kw) ||
            (item.events && item.events[0]?.startTime?.includes(kw)),
    );
  }

  return items.map((item) => {
    const groupedEvents = getEventsGroupedByDate(item);
    const endedEventsCount = groupedEvents.filter((ev) => ev.isEnded).length;

    return {
      ...item,
      groupedEvents,
      allEventsEnded: endedEventsCount === groupedEvents.length,
      someEventsEnded: endedEventsCount > 0,
      moduleName: translateModuleName(item),
      dates: getGroupDates(item, dateFormat),
      datesIso: getGroupDates(item, dateFormatIso),
      location: getLocation(item),
      language: translateLanguage(item),
    };
  });
});

const getLocation = (item) => {
  return item.location === GroupLocation.IN_HOUSE ? t("IN_HOUSE") : item.location;
};

const translateModuleName = (item) => {
  return item.training?.translations?.[locale.value] ?? "";
};

const translateLanguage = (item) => {
  return getGroupLangOptions(t).find((opt) => opt.value === item.language)?.text ?? item.language;
};
</script>

<style lang="scss" scoped>
.event-details {
  padding: 0.5rem;
  position: relative;
  margin: -0.3rem;
  background: var(--p-color-blue-0);
  border-left: 0.5rem solid var(--p-color-blue-600);

  &::after {
    content: "";
    width: 100%;
    height: 0.5rem;
    bottom: 0.5rem;
    left: 0;
    position: absolute;
    background: linear-gradient(0deg, var(--p-color-blue-0), transparent);
  }

  & > div {
    max-height: 275px;
    overflow-y: auto;

    .event-ended {
      opacity: 40%;
    }

    .event-row {
      margin-bottom: 0.5rem;
    }
  }
}
</style>
